/**
 * Comprehensive Validation Utilities
 * Indian Government Compliance Formats
 */

// ============= REGEX PATTERNS =============

// PAN: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// GSTIN: 2 digits + PAN + 1 alphanumeric + Z + 1 alphanumeric (e.g., 22AAAAA0000A1Z5)
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// IFSC: 4 letters + 0 + 6 alphanumerics (e.g., SBIN0001234)
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

// Aadhaar: 12 digits (e.g., 1234 5678 9012)
export const AADHAAR_REGEX = /^[2-9]{1}[0-9]{11}$/;

// Indian PIN Code: 6 digits starting with 1-9
export const PINCODE_REGEX = /^[1-9][0-9]{5}$/;

// Indian Phone: 10 digits starting with 6-9
export const PHONE_REGEX = /^[6-9][0-9]{9}$/;

// Email
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Bank Account: 9-18 digits
export const BANK_ACCOUNT_REGEX = /^[0-9]{9,18}$/;

// MSME/Udyam Number: UDYAM-XX-00-0000000
export const UDYAM_REGEX = /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/i;

// DIPP Startup Number
export const DIPP_REGEX = /^DIPP[0-9]{4,10}$/i;

// ============= VALIDATION FUNCTIONS =============

/**
 * Validate PAN Number
 * Format: AAAAA0000A (5 letters + 4 digits + 1 letter)
 */
export const validatePAN = (pan) => {
  if (!pan) return { valid: false, error: 'PAN number is required' };
  const cleanPAN = pan.toUpperCase().trim();
  if (!PAN_REGEX.test(cleanPAN)) {
    return { valid: false, error: 'Invalid PAN format. Expected: AAAAA0000A' };
  }
  return { valid: true, error: null };
};

/**
 * Validate GSTIN
 * Format: 22AAAAA0000A1Z5 (State code + PAN + Entity code + Z + Checksum)
 */
export const validateGSTIN = (gstin, pan = null) => {
  if (!gstin) return { valid: false, error: 'GST number is required' };
  const cleanGSTIN = gstin.toUpperCase().trim();
  // Common UI examples users paste by mistake
  const EXAMPLE_GSTINS = new Set(['22AAAAA0000A1Z5', '22AAAAA0000A1Z4', '27ABCDE1234F1Z5']);
  if (EXAMPLE_GSTINS.has(cleanGSTIN)) {
    return {
      valid: false,
      error: 'That value is only an example. Enter your real 15-character GSTIN from your GST certificate.',
    };
  }
  if (!GSTIN_REGEX.test(cleanGSTIN)) {
    return { valid: false, error: 'Invalid GSTIN format. Expected: 2-digit state + 10-char PAN + entity + Z + check digit' };
  }
  // Cross-validate with PAN if provided
  if (pan) {
    const panFromGSTIN = cleanGSTIN.substring(2, 12);
    const cleanPAN = pan.toUpperCase().trim();
    if (panFromGSTIN !== cleanPAN) {
      return {
        valid: false,
        error: `GSTIN does not match PAN. GSTIN contains "${panFromGSTIN}" but PAN is "${cleanPAN}". Characters 3–12 of GSTIN must equal your PAN.`,
      };
    }
  }
  return { valid: true, error: null };
};

/**
 * Validate IFSC Code
 * Format: AAAA0000000 (4 letters + 0 + 6 alphanumerics)
 */
export const validateIFSC = (ifsc) => {
  if (!ifsc) return { valid: false, error: 'IFSC code is required' };
  const cleanIFSC = ifsc.toUpperCase().trim();
  if (!IFSC_REGEX.test(cleanIFSC)) {
    return { valid: false, error: 'Invalid IFSC format. Expected: AAAA0XXXXXX' };
  }
  return { valid: true, error: null };
};

/**
 * Validate Aadhaar Number
 * Format: 12 digits, first digit cannot be 0 or 1
 */
export const validateAadhaar = (aadhaar) => {
  if (!aadhaar) return { valid: true, error: null }; // Aadhaar is optional
  const cleanAadhaar = aadhaar.replace(/\s/g, '').trim();
  if (!AADHAAR_REGEX.test(cleanAadhaar)) {
    return { valid: false, error: 'Invalid Aadhaar. Must be 12 digits starting with 2-9' };
  }
  return { valid: true, error: null };
};

/**
 * Validate PIN Code
 * Format: 6 digits starting with 1-9
 */
export const validatePincode = (pincode) => {
  if (!pincode) return { valid: false, error: 'PIN code is required' };
  if (!PINCODE_REGEX.test(pincode.trim())) {
    return { valid: false, error: 'Invalid PIN code. Must be 6 digits' };
  }
  return { valid: true, error: null };
};

/**
 * Validate Indian Phone Number
 * Format: 10 digits starting with 6-9
 */
export const validatePhone = (phone) => {
  if (!phone) return { valid: false, error: 'Phone number is required' };
  const cleanPhone = phone.replace(/[\s-]/g, '').trim();
  if (!PHONE_REGEX.test(cleanPhone)) {
    return { valid: false, error: 'Invalid phone. Must be 10 digits starting with 6-9' };
  }
  return { valid: true, error: null };
};

/**
 * Validate Email
 */
export const validateEmail = (email) => {
  if (!email) return { valid: false, error: 'Email is required' };
  if (!EMAIL_REGEX.test(email.trim())) {
    return { valid: false, error: 'Invalid email format' };
  }
  return { valid: true, error: null };
};

/**
 * Validate Bank Account Number
 * Format: 9-18 digits
 */
export const validateBankAccount = (accountNumber) => {
  if (!accountNumber) return { valid: false, error: 'Account number is required' };
  const clean = accountNumber.replace(/\s/g, '').trim();
  if (!BANK_ACCOUNT_REGEX.test(clean)) {
    return { valid: false, error: 'Account number must be 9-18 digits' };
  }
  return { valid: true, error: null };
};

/**
 * Validate MSME/Udyam Number
 * Format: UDYAM-XX-00-0000000
 */
export const validateUdyamNumber = (udyam) => {
  if (!udyam) return { valid: true, error: null }; // Optional
  if (!UDYAM_REGEX.test(udyam.trim())) {
    return { valid: false, error: 'Invalid format. Expected: UDYAM-XX-00-0000000' };
  }
  return { valid: true, error: null };
};

/**
 * Validate DIPP Number
 */
export const validateDIPPNumber = (dipp) => {
  if (!dipp) return { valid: true, error: null }; // Optional
  if (!DIPP_REGEX.test(dipp.trim())) {
    return { valid: false, error: 'Invalid DIPP format. Expected: DIPP followed by numbers' };
  }
  return { valid: true, error: null };
};

/**
 * Validate Required Field
 */
export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return { valid: false, error: `${fieldName} is required` };
  }
  return { valid: true, error: null };
};

/**
 * Validate Minimum Length
 */
export const validateMinLength = (value, minLength, fieldName) => {
  if (!value) return { valid: false, error: `${fieldName} is required` };
  if (value.length < minLength) {
    return { valid: false, error: `${fieldName} must be at least ${minLength} characters` };
  }
  return { valid: true, error: null };
};

/**
 * Validate Password Strength
 */
export const validatePassword = (password) => {
  if (!password) return { valid: false, error: 'Password is required' };
  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one lowercase letter' };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one number' };
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one special character' };
  }
  return { valid: true, error: null };
};

/**
 * Validate Date (not in past for certain fields)
 */
export const validateFutureDate = (date, fieldName) => {
  if (!date) return { valid: false, error: `${fieldName} is required` };
  const inputDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (inputDate < today) {
    return { valid: false, error: `${fieldName} cannot be in the past` };
  }
  return { valid: true, error: null };
};

/**
 * Validate Date (not in future for incorporation date)
 */
export const validatePastDate = (date, fieldName) => {
  if (!date) return { valid: true, error: null }; // Optional
  const inputDate = new Date(date);
  const today = new Date();
  if (inputDate > today) {
    return { valid: false, error: `${fieldName} cannot be in the future` };
  }
  return { valid: true, error: null };
};

/**
 * Validate Turnover (positive number)
 */
export const validateTurnover = (value) => {
  if (!value) return { valid: true, error: null }; // Optional
  const num = parseFloat(value);
  if (isNaN(num) || num < 0) {
    return { valid: false, error: 'Turnover must be a positive number' };
  }
  return { valid: true, error: null };
};

/**
 * Validate Employee Count
 */
export const validateEmployeeCount = (value) => {
  if (!value) return { valid: true, error: null }; // Optional
  const num = parseInt(value);
  if (isNaN(num) || num < 0) {
    return { valid: false, error: 'Employee count must be a positive number' };
  }
  return { valid: true, error: null };
};

// ============= STEP-WISE VALIDATION FOR VENDOR REGISTRATION =============

/**
 * Validate Step 1: Basic Details
 */
export const validateStep1 = (data) => {
  const errors = {};
  
  const companyName = validateRequired(data.company_name, 'Company name');
  if (!companyName.valid) errors.company_name = companyName.error;
  
  const businessType = validateRequired(data.business_type, 'Business type');
  if (!businessType.valid) errors.business_type = businessType.error;
  
  const pan = validatePAN(data.pan_number);
  if (!pan.valid) errors.pan_number = pan.error;
  
  const gstin = validateGSTIN(data.gst_number, data.pan_number);
  if (!gstin.valid) errors.gst_number = gstin.error;
  
  const incDate = validatePastDate(data.incorporation_date, 'Incorporation date');
  if (!incDate.valid) errors.incorporation_date = incDate.error;
  
  const turnover = validateTurnover(data.annual_turnover);
  if (!turnover.valid) errors.annual_turnover = turnover.error;
  
  const empCount = validateEmployeeCount(data.employee_count);
  if (!empCount.valid) errors.employee_count = empCount.error;
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate Step 2: Address
 */
export const validateStep2 = (registered, correspondence, sameAsRegistered) => {
  const errors = { registered: {}, correspondence: {} };
  
  // Registered Address
  const regAddr1 = validateRequired(registered.address_line1, 'Address line 1');
  if (!regAddr1.valid) errors.registered.address_line1 = regAddr1.error;
  
  const regCity = validateRequired(registered.city, 'City');
  if (!regCity.valid) errors.registered.city = regCity.error;
  
  const regState = validateRequired(registered.state, 'State');
  if (!regState.valid) errors.registered.state = regState.error;
  
  const regPincode = validatePincode(registered.pincode);
  if (!regPincode.valid) errors.registered.pincode = regPincode.error;
  
  // Correspondence Address (only if different)
  if (!sameAsRegistered) {
    const corrAddr1 = validateRequired(correspondence.address_line1, 'Address line 1');
    if (!corrAddr1.valid) errors.correspondence.address_line1 = corrAddr1.error;
    
    const corrCity = validateRequired(correspondence.city, 'City');
    if (!corrCity.valid) errors.correspondence.city = corrCity.error;
    
    const corrState = validateRequired(correspondence.state, 'State');
    if (!corrState.valid) errors.correspondence.state = corrState.error;
    
    const corrPincode = validatePincode(correspondence.pincode);
    if (!corrPincode.valid) errors.correspondence.pincode = corrPincode.error;
  }
  
  const hasErrors = Object.keys(errors.registered).length > 0 || 
                    (!sameAsRegistered && Object.keys(errors.correspondence).length > 0);
  
  return { valid: !hasErrors, errors };
};

/**
 * Validate Step 3: Authorized Signatory
 */
export const validateStep3 = (data) => {
  const errors = {};
  
  const name = validateRequired(data.name, 'Full name');
  if (!name.valid) errors.name = name.error;
  
  const designation = validateRequired(data.designation, 'Designation');
  if (!designation.valid) errors.designation = designation.error;
  
  const email = validateEmail(data.email);
  if (!email.valid) errors.email = email.error;
  
  const phone = validatePhone(data.phone);
  if (!phone.valid) errors.phone = phone.error;
  
  const aadhaar = validateAadhaar(data.aadhaar_number);
  if (!aadhaar.valid) errors.aadhaar_number = aadhaar.error;
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate Step 4: Bank Details
 */
export const validateStep4 = (data) => {
  const errors = {};
  
  const bankName = validateRequired(data.bank_name, 'Bank name');
  if (!bankName.valid) errors.bank_name = bankName.error;
  
  const branchName = validateRequired(data.branch_name, 'Branch name');
  if (!branchName.valid) errors.branch_name = branchName.error;
  
  const accountNumber = validateBankAccount(data.account_number);
  if (!accountNumber.valid) errors.account_number = accountNumber.error;
  
  const ifsc = validateIFSC(data.ifsc_code);
  if (!ifsc.valid) errors.ifsc_code = ifsc.error;
  
  const accountType = validateRequired(data.account_type, 'Account type');
  if (!accountType.valid) errors.account_type = accountType.error;
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate Step 5: Documents
 */
export const validateStep5 = (documents) => {
  const errors = {};
  
  // Required documents
  const panDoc = documents.find(d => d.document_type === 'pan');
  if (!panDoc) errors.pan = 'PAN Card document is required';
  
  const gstDoc = documents.find(d => d.document_type === 'gst');
  if (!gstDoc) errors.gst = 'GST Certificate is required';
  
  const chequeDoc = documents.find(d => d.document_type === 'cancelled_cheque');
  if (!chequeDoc) errors.cancelled_cheque = 'Cancelled cheque is required';
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate Step 6: Capability
 */
export const validateStep6 = (data) => {
  const errors = {};
  
  if (!data.categories || data.categories.length === 0) {
    errors.categories = 'Select at least one category';
  }
  
  if (data.is_msme && data.msme_number) {
    const udyam = validateUdyamNumber(data.msme_number);
    if (!udyam.valid) errors.msme_number = udyam.error;
  }
  
  if (data.is_startup && data.startup_dpiit_number) {
    const dpiit = validateDIPPNumber(data.startup_dpiit_number);
    if (!dpiit.valid) errors.startup_dpiit_number = dpiit.error;
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate Step 7: Declaration
 */
export const validateStep7 = (data) => {
  const errors = {};
  
  // Blacklisting - if "Yes" (not_blacklisted is false), details are required
  if (!data.not_blacklisted && !data.blacklisted_details) {
    errors.blacklisted_details = 'Please provide blacklisting details';
  }
  if (!data.no_conflict_of_interest) errors.no_conflict_of_interest = 'This declaration is required';
  if (!data.information_correct) errors.information_correct = 'This declaration is required';
  if (!data.accept_terms) errors.accept_terms = 'You must accept the terms and conditions';
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

// ============= HELPER COMPONENT FOR ERROR DISPLAY =============

/**
 * Get error class for input styling
 */
export const getErrorClass = (error) => {
  return error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';
};

export default {
  validatePAN,
  validateGSTIN,
  validateIFSC,
  validateAadhaar,
  validatePincode,
  validatePhone,
  validateEmail,
  validateBankAccount,
  validateUdyamNumber,
  validateDIPPNumber,
  validateRequired,
  validateMinLength,
  validatePassword,
  validateFutureDate,
  validatePastDate,
  validateTurnover,
  validateEmployeeCount,
  validateStep1,
  validateStep2,
  validateStep3,
  validateStep4,
  validateStep5,
  validateStep6,
  validateStep7,
  getErrorClass,
};

// ============= PII MASKING UTILITIES =============

/**
 * Mask PAN number - Show only last 4 characters
 * ABCDE1234F -> XXXXXX234F
 */
export const maskPAN = (pan) => {
  if (!pan || pan.length < 4) return pan;
  return 'XXXXXX' + pan.slice(-4);
};

/**
 * Mask Aadhaar number - Show only last 4 digits
 * 123456789012 -> XXXX XXXX 9012
 */
export const maskAadhaar = (aadhaar) => {
  if (!aadhaar || aadhaar.length < 4) return aadhaar;
  const cleaned = aadhaar.replace(/\s/g, '');
  return 'XXXX XXXX ' + cleaned.slice(-4);
};

/**
 * Mask Bank Account number - Show only last 4 digits
 * 123456789012 -> XXXXXXXX9012
 */
export const maskBankAccount = (account) => {
  if (!account || account.length < 4) return account;
  return 'X'.repeat(account.length - 4) + account.slice(-4);
};

/**
 * Mask GSTIN - Show only first 2 and last 3 characters
 * 22AAAAA0000A1Z5 -> 22XXXXXXXXXZ5
 */
export const maskGSTIN = (gstin) => {
  if (!gstin || gstin.length < 5) return gstin;
  return gstin.slice(0, 2) + 'X'.repeat(gstin.length - 5) + gstin.slice(-3);
};

/**
 * Mask Phone number - Show only last 4 digits
 * 9876543210 -> XXXXXX3210
 */
export const maskPhone = (phone) => {
  if (!phone || phone.length < 4) return phone;
  return 'X'.repeat(phone.length - 4) + phone.slice(-4);
};

/**
 * Mask Email - Show first 2 chars and domain
 * example@domain.com -> ex***@domain.com
 */
export const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  if (local.length <= 2) return email;
  return local.slice(0, 2) + '***@' + domain;
};

/**
 * A sensitive field can be toggled only when there is a real value to reveal.
 */
export const canToggleSensitiveValue = (value) =>
  value != null && String(value).trim() !== '';

/** Shown until the reviewer clicks the eye. The full value is never visible by default. */
export const HIDDEN_SENSITIVE_VALUE = '••••••••••';

/**
 * Keep PII hidden until the reviewer chooses to reveal it, then hide it again.
 */
export const displaySensitiveValue = (value, _maskValue, revealed) => {
  if (!canToggleSensitiveValue(value)) return value;
  if (revealed) return value;
  return HIDDEN_SENSITIVE_VALUE;
};

